import type { Config, UponSanitizeAttributeHook } from 'dompurify'
import DOMPurify from 'dompurify'
import { getVideoFirstFrameUrl } from './mediaUrl'

/**
 * 角色简介 HTML 的解析 / 消毒 / 校验工具（纯数据处理，不含渲染）。
 *
 * 信任模型：
 * - <html-box> 包裹的内容 = 允许执行脚本/嵌套 iframe 的交互 HTML，
 *   原样保留，安全边界由渲染层 sandbox="allow-scripts"（opaque origin）承担，
 *   内容本身必须经过审核（审核方以 html-box 为边界定位需要审查的代码段）；
 * - 未被 html-box 包裹的内容 = 不可信，走 DOMPurify 严格白名单消毒，
 *   未包裹的 script/iframe 降级为可见纯文本（不执行、不静默吞掉）。
 */

export const HTML_BOX_TAG = 'html-box'
export const MESSAGE_TAG = 'message'
const NARRATION_TAG = 'narration'

/** 简介顶层分段：message/narration/松散文本按文本段提取，html-box 与其余内容按 HTML 段分组 */
export type IntroSegment =
  | { id: string; type: 'message'; text: string }
  | { id: string; type: 'narration'; text: string }
  | { id: string; type: 'text'; text: string }
  | { id: string; type: 'html'; html: string }

const HTML_INTRODUCTION_PURIFY_CONFIG: Config = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'u',
    'b',
    'i',
    's',
    'del',
    'ins',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    'pre',
    'code',
    'a',
    'span',
    'div',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'hr',
    'img',
    'audio',
    'video',
    'source',
  ],
  ALLOWED_ATTR: [
    'href',
    'title',
    'class',
    'id',
    'style',
    'src',
    'alt',
    'width',
    'height',
    // audio/video/source 相关属性
    'controls',
    'loop',
    'muted',
    'playsinline',
    'preload',
    'poster',
    'type',
  ],
  ALLOW_DATA_ATTR: false,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
}

function isAllowedHrefProtocol(value: string): boolean {
  if (typeof window === 'undefined') return false

  const trimmed = value.trim()
  if (!trimmed) return false

  try {
    const url = new URL(trimmed, window.location.origin)
    return ['http:', 'https:', 'mailto:', 'tel:', 'sms:'].includes(url.protocol)
  } catch {
    return false
  }
}

function isAllowedImageSrc(value: string): boolean {
  if (typeof window === 'undefined') return false

  const trimmed = value.trim()
  if (!trimmed) return false

  if (/^data:image\/(?:png|jpe?g|gif|webp|avif|bmp);base64,[a-z0-9+/=\s]+$/i.test(trimmed)) {
    return true
  }

  if (!/^https?:\/\//i.test(trimmed)) {
    return false
  }

  try {
    const url = new URL(trimmed)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function isAllowedMediaSrc(value: string): boolean {
  if (typeof window === 'undefined') return false

  const trimmed = value.trim()
  if (!trimmed) return false

  // data: 仅允许 audio / video 的 base64
  if (/^data:(?:audio|video)\/[a-z0-9.+-]+;base64,[a-z0-9+/=\s]+$/i.test(trimmed)) {
    return true
  }

  if (!/^https?:\/\//i.test(trimmed)) {
    return false
  }

  try {
    const url = new URL(trimmed)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function isAllowedInlineStyle(value: string): boolean {
  const normalized = value.trim().toLowerCase()

  if (!normalized) return false

  return ![
    /url\s*\(/,
    /@import/,
    /expression\s*\(/,
    /behavior\s*:/,
    /-moz-binding\s*:/,
    /javascript\s*:/,
  ].some((pattern) => pattern.test(normalized))
}

const sanitizeHtmlAttribute: UponSanitizeAttributeHook = (node, hookEvent) => {
  const tagName = node.nodeName.toLowerCase()
  const attrName = hookEvent.attrName.toLowerCase()

  if (attrName === 'style' && !isAllowedInlineStyle(hookEvent.attrValue)) {
    hookEvent.keepAttr = false
    return
  }

  if (attrName === 'href' && !isAllowedHrefProtocol(hookEvent.attrValue)) {
    hookEvent.keepAttr = false
    return
  }

  const isMediaTag = tagName === 'audio' || tagName === 'video'

  // alt 仅 img
  if (attrName === 'alt' && tagName !== 'img') {
    hookEvent.keepAttr = false
    return
  }

  // width/height 仅 img / video
  if ((attrName === 'width' || attrName === 'height') && tagName !== 'img' && tagName !== 'video') {
    hookEvent.keepAttr = false
    return
  }

  // poster 仅 video
  if (attrName === 'poster' && tagName !== 'video') {
    hookEvent.keepAttr = false
    return
  }

  // 媒体布尔/枚举属性仅 audio / video
  if (['controls', 'loop', 'muted', 'playsinline', 'preload'].includes(attrName) && !isMediaTag) {
    hookEvent.keepAttr = false
    return
  }

  // src 只允许出现在 img / audio / video / source
  if (
    attrName === 'src' &&
    tagName !== 'img' &&
    tagName !== 'audio' &&
    tagName !== 'video' &&
    tagName !== 'source'
  ) {
    hookEvent.keepAttr = false
    return
  }

  // src 值按标签分流校验：img 走图片校验，媒体标签走媒体校验
  if (attrName === 'src') {
    const ok =
      tagName === 'img'
        ? isAllowedImageSrc(hookEvent.attrValue)
        : isAllowedMediaSrc(hookEvent.attrValue)
    if (!ok) {
      hookEvent.keepAttr = false
      return
    }
  }

  // poster 走图片校验
  if (attrName === 'poster' && !isAllowedImageSrc(hookEvent.attrValue)) {
    hookEvent.keepAttr = false
    return
  }

  if (
    (attrName === 'width' || attrName === 'height') &&
    !/^\d{1,5}$/.test(hookEvent.attrValue.trim())
  ) {
    hookEvent.keepAttr = false
  }
}

/** 无 poster 的视频使用第一帧作为封面，避免浏览器显示默认的拉伸占位图。 */
function addVideoFirstFrameFallback(html: string): string {
  if (typeof document === 'undefined') return html

  const template = document.createElement('template')
  template.innerHTML = html

  for (const video of template.content.querySelectorAll('video:not([poster])')) {
    const src = video.getAttribute('src')
    if (src) video.setAttribute('src', getVideoFirstFrameUrl(src))

    for (const source of video.querySelectorAll('source[src]')) {
      const sourceSrc = source.getAttribute('src')
      if (sourceSrc) source.setAttribute('src', getVideoFirstFrameUrl(sourceSrc))
    }
  }

  return template.innerHTML
}

/**
 * 严格消毒（用于未包裹 html-box 的不可信内容）：
 * DOMPurify 白名单剥掉 script/事件处理器/javascript: URL/meta 刷新等一切可执行面，
 * 调用方需先用 demoteUnwrappedExecutableTags 把 script/iframe 转为可见文本再进来。
 */
function sanitizeIntroductionFragment(html: string): string | null {
  if (!html) return null

  try {
    DOMPurify.addHook('uponSanitizeAttribute', sanitizeHtmlAttribute)
    const sanitizedHtml = DOMPurify.sanitize(html, HTML_INTRODUCTION_PURIFY_CONFIG)
    return addVideoFirstFrameFallback(sanitizedHtml)
  } catch (error) {
    console.error('HTML sanitization failed:', error)
    return null
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute', sanitizeHtmlAttribute)
  }
}

function hasHtmlBoxAncestor(element: Element): boolean {
  let parent: Element | null = element.parentElement
  while (parent) {
    if (parent.tagName.toLowerCase() === HTML_BOX_TAG) return true
    parent = parent.parentElement
  }
  return false
}

/** 未包裹在 html-box 内的 script/iframe 降级为可见纯文本（outerHTML 转义后展示） */
function demoteUnwrappedExecutableTags(container: HTMLElement, ownerDocument: Document) {
  for (const element of Array.from(container.querySelectorAll('script, iframe'))) {
    if (!hasHtmlBoxAncestor(element)) {
      element.replaceWith(ownerDocument.createTextNode(element.outerHTML))
    }
  }
}

/**
 * 按文档直接子级拆分简介（DOMParser 解析为惰性文档，脚本不执行、资源不加载）：
 * - <message>/<narration> → 文本段（textContent，标签按文本处理不渲染）；
 * - <html-box> → 交互 HTML 段（innerHTML 原样保留，脚本可执行，需审核兜底）；
 * - 其余相邻节点归并为普通 HTML 段：先降级未包裹的 script/iframe 为可见文本，
 *   再走 DOMPurify 严格消毒（剥掉事件处理器 / javascript: URL / meta 刷新等）；
 *   纯文本的归并结果直接走文本段，避免为一段文字启动整个 iframe。
 *   嵌套在其它元素内部的 html-box 摘出后原样回填，内部脚本不被消毒破坏。
 * html-box 内部嵌套的 message/narration 属于 HTML 文档本身，不做提取。
 */
export function splitIntroSegments(htmlData: string): IntroSegment[] {
  if (typeof window === 'undefined' || !htmlData) return []

  try {
    const parsed = new DOMParser().parseFromString(htmlData, 'text/html')
    const segments: IntroSegment[] = []
    let pendingNodes: Node[] = []

    const flushPending = () => {
      if (pendingNodes.length === 0) return
      // 容器必须建在惰性文档里（而非 document.createElement）：保证消毒前的所有
      // DOM 操作严格不接触活文档，节点不会因被 adopt 进活跃文档而触发资源加载
      const container = parsed.createElement('div')
      container.append(...pendingNodes)
      demoteUnwrappedExecutableTags(container, parsed)

      // 嵌套在其它元素内部的 html-box：先摘出为占位符文本，消毒后原样回填。
      // 被包裹的内容（含脚本）不经过消毒管线，与顶层 html-box 口径一致。
      const restoreSalt = Math.random().toString(36).slice(2, 8)
      const nestedBoxHtmlList: string[] = []
      for (const box of Array.from(container.querySelectorAll(HTML_BOX_TAG))) {
        nestedBoxHtmlList.push(box.innerHTML)
        box.replaceWith(
          parsed.createTextNode(`htmlbox${restoreSalt}slot${nestedBoxHtmlList.length - 1}end`),
        )
      }

      const text = container.textContent?.trim() ?? ''
      if (container.children.length === 0) {
        if (text) {
          segments.push({ id: `text-${segments.length}`, type: 'text', text })
        }
      } else {
        let html = sanitizeIntroductionFragment(container.innerHTML) ?? ''
        nestedBoxHtmlList.forEach((boxHtml, index) => {
          html = html.split(`htmlbox${restoreSalt}slot${index}end`).join(boxHtml)
        })
        if (html.trim()) {
          segments.push({ id: `html-${segments.length}`, type: 'html', html })
        }
      }
      pendingNodes = []
    }

    for (const node of Array.from(parsed.body.childNodes)) {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        pendingNodes.push(node)
        continue
      }

      const tagName = (node as Element).tagName.toLowerCase()
      if (tagName === HTML_BOX_TAG) {
        flushPending()
        const html = addVideoFirstFrameFallback((node as Element).innerHTML.trim())
        if (html) {
          segments.push({ id: `html-${segments.length}`, type: 'html', html })
        }
      } else if (tagName === MESSAGE_TAG || tagName === NARRATION_TAG) {
        flushPending()
        const text = (node as Element).textContent?.trim() ?? ''
        if (text) {
          segments.push(
            tagName === MESSAGE_TAG
              ? { id: `message-${segments.length}`, type: 'message', text }
              : { id: `narration-${segments.length}`, type: 'narration', text },
          )
        }
      } else {
        pendingNodes.push(node)
      }
    }
    flushPending()

    return segments
  } catch {
    return []
  }
}

/**
 * 创建侧校验：内容中是否存在未被 html-box 包裹的 script/iframe。
 * 返回第一个违规标签名（'script' | 'iframe'），无违规返回 null。
 */
export function findUnwrappedHtmlBoxTag(htmlData: string): 'script' | 'iframe' | null {
  if (typeof window === 'undefined' || !htmlData) return null

  try {
    const parsed = new DOMParser().parseFromString(htmlData, 'text/html')
    for (const element of Array.from(parsed.body.querySelectorAll('script, iframe'))) {
      if (!hasHtmlBoxAncestor(element)) {
        return element.tagName.toLowerCase() as 'script' | 'iframe'
      }
    }
  } catch {
    return null
  }

  return null
}

const RENDERABLE_CACHE_MAX = 100
const renderableCache = new Map<string, boolean>()

/** 结构性可渲染判定：只看有无可见文本 / 媒体 src（不再校验 URL 合法性） */
function isRenderableElement(element: Element): boolean {
  const tagName = element.tagName.toLowerCase()

  if (tagName === 'br' || tagName === 'hr') {
    return true
  }

  if (tagName === 'img') {
    return element.hasAttribute('src')
  }

  if (tagName === 'audio' || tagName === 'video') {
    if (element.hasAttribute('src')) return true
    return Array.from(element.querySelectorAll('source')).some((s) => s.hasAttribute('src'))
  }

  return Boolean(
    element.textContent?.trim() || Array.from(element.children).some(isRenderableElement),
  )
}

/**
 * 是否按 HTML 卡片渲染：仅认元素内容（与旧版消毒后的判定一致），
 * 纯文本 / 仅未包裹脚本的内容返回 false，走调用方的纯文本路径。
 * 结果按内容字符串缓存（列表页会在每次 render 时调用）。
 */
export function isRenderableHtml(htmlData: string): boolean {
  if (typeof document === 'undefined') return false
  if (!htmlData) return false

  const cached = renderableCache.get(htmlData)
  if (cached !== undefined) return cached

  let result = false
  try {
    const parsed = new DOMParser().parseFromString(htmlData, 'text/html')

    for (const element of Array.from(parsed.body.querySelectorAll('script, iframe'))) {
      // 未包裹的可执行标签不产生视觉内容，不计入可渲染判定
      if (!hasHtmlBoxAncestor(element)) element.remove()
    }
    for (const box of Array.from(parsed.body.querySelectorAll(HTML_BOX_TAG))) {
      box.replaceWith(...box.childNodes)
    }

    result = Array.from(parsed.body.children).some(isRenderableElement)
  } catch {
    result = false
  }

  renderableCache.set(htmlData, result)
  if (renderableCache.size > RENDERABLE_CACHE_MAX) {
    const oldestKey = renderableCache.keys().next().value
    if (oldestKey !== undefined) renderableCache.delete(oldestKey)
  }
  return result
}
